
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InputWithSuggestions } from '@/components/ui/input-with-suggestions';
import { useDebouncedQuery } from '@/hooks/useDebouncedQuery';
import { fetchAddressSuggestions, AddressSuggestion } from '@/lib/fetchAddressSuggestions';
import { useToast } from '@/hooks/use-toast';

interface AddressInputProps {
  address: string;
  onAddressChange: (address: string) => void;
  className?: string;
  showLabel?: boolean;
}

export function AddressInput({
  address,
  onAddressChange,
  className,
  showLabel = true
}: AddressInputProps) {
  const [searchValue, setSearchValue] = useState(address);
  const { toast } = useToast();

  // Mount diagnostic
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      console.debug('[AddressInput] mounted with initial address:', address);
    }
  }, []);

  const debouncedSearchValue = useDebouncedQuery(searchValue, 500);
  const enabled = debouncedSearchValue.length >= 3;
  // Lightweight diagnostics to verify query triggering (Vite: use import.meta.env.DEV)
  if (import.meta.env.DEV) {
    console.debug('[AddressInput] value:', searchValue, 'debounced:', debouncedSearchValue, 'enabled:', enabled);
  }

  // Trace debounced changes explicitly
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      console.debug('[AddressInput] debounced changed ->', debouncedSearchValue, 'enabled:', enabled);
    }
  }, [debouncedSearchValue, enabled]);

  const { data: addressSuggestions = [], isLoading, error } = useQuery({
    queryKey: ['address-suggestions', debouncedSearchValue],
    queryFn: () => fetchAddressSuggestions(debouncedSearchValue),
    enabled,
    onSuccess: (data) => {
      if (import.meta.env.DEV) {
        console.debug('[AddressInput] suggestions fetched:', Array.isArray(data) ? data.length : 'n/a');
      }
    },
    onError: (err) => {
      if (import.meta.env.DEV) {
        console.debug('[AddressInput] query error:', err);
      }
    },
    onSettled: () => {
      if (import.meta.env.DEV) {
        console.debug('[AddressInput] query settled for:', debouncedSearchValue);
      }
    }
  });

  const handleAddressChange = (value: string) => {
    setSearchValue(value);
    onAddressChange(value);
  };

  const handleAddressSelect = (suggestion: { id: string; value: string; label: string; description?: string }) => {
    const addressSuggestion = addressSuggestions.find(addr => addr.value === suggestion.value);
    if (addressSuggestion) {
      onAddressChange(addressSuggestion.value);
      setSearchValue(addressSuggestion.value);
      
      toast({
        title: "Адрес выбран",
        description: addressSuggestion.value,
      });
    }
  };

  const suggestions = addressSuggestions.map((addr, index) => ({
    id: `addr-${index}`,
    value: addr.value,
    label: addr.value,
    description: addr.data.postal_code ? `Индекс: ${addr.data.postal_code}` : undefined
  }));

  if (error) {
    toast({
      title: "Ошибка загрузки адресов",
      description: "Не удалось загрузить адресные подсказки",
      variant: "destructive",
    });
  }

  return (
    <div className={className}>
      <InputWithSuggestions
        id="address"
        label={showLabel ? "Адрес прописки" : undefined}
        value={searchValue}
        onChange={handleAddressChange}
        onSelect={handleAddressSelect}
        suggestions={suggestions}
        loading={isLoading}
        placeholder="Начните вводить адрес..."
      />
    </div>
  );
}
